import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BedDouble, Maximize, MapPin, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Reveal } from "../components/Reveal";

const fieldCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-sm font-light text-azur outline-none transition-colors focus:border-azur";

export default function PropertyDetail() {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [imgIdx, setImgIdx] = useState(0);
  const [lead, setLead] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setProperty(null);
    setNotFound(false);
    setImgIdx(0);
    api
      .get(`/properties/${id}`)
      .then((r) => {
        setProperty(r.data);
        document.title = `${r.data.title} — Kemelia Estate`;
      })
      .catch(() => setNotFound(true));
    return () => {
      document.title = "Kemelia Estate — Agence Immobilière · Côte d'Azur";
    };
  }, [id]);

  const gallery = useMemo(
    () =>
      property
        ? [property.image, ...(property.gallery || []).filter((g) => g && g !== property.image)]
        : [],
    [property]
  );

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads", {
        kind: "contact",
        ...lead,
        details: { bien: property.title, reference: property.id },
      });
      toast.success("Votre demande a bien été envoyée. Nous revenons vers vous très vite.");
      setLead({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Une erreur est survenue, veuillez réessayer.");
    } finally {
      setSending(false);
    }
  };

  if (notFound) {
    return (
      <section className="flex min-h-screen flex-col items-center justify-center px-4 pt-24 text-center">
        <h1 className="font-serif text-3xl text-azur">Ce bien n'est plus disponible.</h1>
        <Link to="/biens" data-testid="detail-back-notfound" className="mt-6 rounded-full bg-azur px-7 py-3.5 text-sm font-medium text-sand transition-colors hover:bg-terracotta">
          Voir nos biens
        </Link>
      </section>
    );
  }

  if (!property) {
    return (
      <section className="flex min-h-screen items-center justify-center pt-24">
        <span className="font-serif text-2xl italic text-slate-400">Chargement…</span>
      </section>
    );
  }

  return (
    <section data-testid="property-detail-page" className="mx-auto max-w-7xl px-4 pb-24 pt-28 sm:px-8 lg:px-16 lg:pt-36">
      <Reveal>
        <Link
          to="/biens"
          data-testid="detail-back-link"
          className="group inline-flex items-center gap-2 text-sm font-light text-slate-500 transition-colors hover:text-azur"
        >
          <ArrowLeft size={16} className="transition-transform duration-300 group-hover:-translate-x-1" />
          Retour aux biens
        </Link>
      </Reveal>

      <div className="mt-8 grid gap-12 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <div className="relative aspect-[4/5] overflow-hidden rounded-2xl sm:aspect-[4/3] lg:aspect-[4/5]">
            <motion.img
              key={gallery[imgIdx]}
              src={gallery[imgIdx]}
              alt={property.title}
              data-testid="detail-gallery-image"
              initial={{ opacity: 0.4 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="absolute inset-0 h-full w-full object-cover"
            />
            {gallery.length > 1 && (
              <>
                <button
                  onClick={() => setImgIdx((imgIdx - 1 + gallery.length) % gallery.length)}
                  data-testid="detail-gallery-prev"
                  aria-label="Photo précédente"
                  className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/85 p-2.5 text-azur backdrop-blur transition-colors hover:bg-gold"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  onClick={() => setImgIdx((imgIdx + 1) % gallery.length)}
                  data-testid="detail-gallery-next"
                  aria-label="Photo suivante"
                  className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/85 p-2.5 text-azur backdrop-blur transition-colors hover:bg-gold"
                >
                  <ChevronRight size={18} />
                </button>
                <span data-testid="detail-gallery-counter" className="absolute bottom-4 right-4 rounded-full bg-slate-950/60 px-3 py-1 font-mono text-[10px] tracking-[0.2em] text-white">
                  {imgIdx + 1} / {gallery.length}
                </span>
              </>
            )}
          </div>
          {gallery.length > 1 && (
            <div data-testid="detail-gallery-thumbs" className="no-scrollbar mt-3 flex gap-2 overflow-x-auto pb-1">
              {gallery.map((g, i) => (
                <button
                  key={g + i}
                  onClick={() => setImgIdx(i)}
                  data-testid={`detail-thumb-${i}`}
                  className={`h-16 w-20 shrink-0 overflow-hidden rounded-lg border-2 transition-all ${
                    i === imgIdx ? "border-gold" : "border-transparent opacity-60 hover:opacity-100"
                  }`}
                >
                  <img src={g} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </Reveal>

        <Reveal delay={0.15}>
          <span className="font-mono text-xs font-medium uppercase tracking-[0.25em] text-terracotta">
            {property.type} — {property.location}
          </span>
          <h1 className="mt-4 font-serif text-3xl font-normal leading-tight tracking-tight text-azur sm:text-4xl">
            {property.title}
          </h1>
          <div className="mt-5 font-serif text-3xl font-semibold text-azur sm:text-4xl">{property.price}</div>

          <div className="mt-6 flex flex-wrap items-center gap-5 border-y border-slate-200 py-5 text-sm font-light text-slate-600">
            <span className="flex items-center gap-2">
              <BedDouble size={16} className="text-gold" /> {property.bedrooms} chambres
            </span>
            <span className="flex items-center gap-2">
              <Maximize size={16} className="text-gold" /> {property.surface}
            </span>
            <span className="flex items-center gap-2">
              <MapPin size={16} className="text-gold" /> {property.location}
            </span>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            {property.tags?.map((t) => (
              <span key={t} className="rounded-full bg-sand px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-azur">
                {t}
              </span>
            ))}
          </div>

          <div data-testid="detail-description" className="mt-6 space-y-4">
            {property.description.split("\n").filter((l) => l.trim()).map((line, i) => {
              const parts = line.split(/(\*\*.+?\*\*)/g).map((part, j) =>
                part.startsWith("**") && part.endsWith("**") ? (
                  <strong key={j} className="font-semibold text-azur">{part.slice(2, -2)}</strong>
                ) : (
                  part
                )
              );
              return line.trim().startsWith("###") ? (
                <h3 key={i} className="font-serif text-xl font-semibold text-azur">
                  {line.trim().replace(/^#+\s*/, "")}
                </h3>
              ) : (
                <p key={i} className="text-lg font-light leading-relaxed text-slate-600">{parts}</p>
              );
            })}
          </div>

          <form
            onSubmit={submit}
            data-testid="detail-contact-form"
            className="mt-10 rounded-2xl border border-slate-200/80 bg-white p-7 shadow-[0_4px_20px_rgba(0,0,0,0.03)]"
          >
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
              Intéressé par ce bien ?
            </span>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <input required placeholder="Nom" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} data-testid="detail-contact-name" className={fieldCls} />
              <input required type="email" placeholder="Email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} data-testid="detail-contact-email" className={fieldCls} />
            </div>
            <input placeholder="Téléphone" value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} data-testid="detail-contact-phone" className={fieldCls + " mt-3"} />
            <textarea placeholder="Votre message" rows={3} value={lead.message} onChange={(e) => setLead({ ...lead, message: e.target.value })} data-testid="detail-contact-message" className={fieldCls + " mt-3 resize-none"} />
            <button
              type="submit"
              disabled={sending}
              data-testid="detail-contact-submit"
              className="mt-5 w-full rounded-xl bg-azur px-6 py-4 text-sm font-medium text-sand transition-colors duration-300 hover:bg-terracotta disabled:opacity-60"
            >
              {sending ? "Envoi en cours…" : "Être contacté pour ce bien"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
